import {useEffect, useMemo, useRef, useState} from 'react';
import {usePolling} from '../api';
import {MoleculeChip, StructureModal} from '../MoleculeChip';

const STAGES = ['S1_symmetry', 'S2_alignn_gap', 'S3_vasp_relax',
  'S4_vasp_magnetic', 'S5_mp_hull', 'S6_prophet_phonons', 'S7_qe_relax_pm',
  'S8_qe_phonons', 'S9_epw_tc'];
const STAGE_SHORT = ['S1 对称性', 'S2 ALIGNN gap', 'S3 VASP relax',
  'S4 磁性分支', 'S5 MP hull', 'S6 Prophet 声子', 'S7 QE relax P/M',
  'S8 QE 声子', 'S9 EPW Tc'];

/* 与 scheduler 一致的执行语义：
   - batch：整批一个 job（K8s 批量 / GPU chunk），全部分子同时进同时出
   - case：每个分子独立 Job，全局并发上限 = kubernetes.max_parallel_jobs (8) */
const STAGE_KINDS = ['batch', 'batch', 'case', 'case', 'batch', 'batch',
  'case', 'case', 'case'];
const MAX_RUNNING = 8;

/* 每关跑在哪种机器上（与 registry.py backend 一致）：
   CPU = K8s CPU 集群（VASP/QE 第一性原理）；GPU = 17501 GPU 机（AI 推理）。
   S7 行内混有 S7e（AI 声子数据，GPU），其余子步为 CPU。 */
const STAGE_HW = ['CPU', 'GPU', 'CPU', 'CPU', 'CPU', 'GPU', 'CPU+GPU',
  'CPU', 'CPU'];
const STAGE_HW_TITLE = {
  CPU: 'CPU 集群（K8s）：VASP / QE 第一性原理计算',
  GPU: 'GPU 机器（17501）：AI 模型推理',
  'CPU+GPU': 'CPU 为主，其中「AI 声子数据」一步跑在 GPU 机器（17501）',
};

/* 候选状态外行视图：9 个后端状态归并为 5 组（0 也显示，置灰）。
   原始状态与 bcsui.models.Candidate.STATUS 一致，悬停可见细分。 */
const STATUS_GROUPS = [
  {key: 'pending', name: '排队等待', items: ['pending']},
  {key: 'running', name: '筛选中', items: ['active', 'passed']},
  {key: 'rejected', name: '已淘汰', items: ['rejected']},
  {key: 'issues', name: '出问题待处理',
   items: ['flagged', 'unresolved', 'quarantine', 'needs_human']},
  {key: 'done', name: '完成', items: ['done']},
];
const STATUS_SUB_HUMAN = {
  pending: '排队', active: '计算中', passed: '等下一关', rejected: '淘汰',
  flagged: '待复查', unresolved: '查无结论', quarantine: '已隔离',
  needs_human: '需人工', done: '完成',
};

/* ---- 最新动态：把事件/候选终态翻译成外行能懂的话 ---- */
const STAGE_HUMAN = {
  S1_symmetry: '结构预检查', S2_alignn_gap: 'AI 带隙初筛', S3_vasp_relax: '结构精修',
  S4_vasp_magnetic: '磁性检查', S5_mp_hull: '能量稳定性检查',
  S6_prophet_phonons: 'AI 声子预筛', S7_qe_relax_pm: '第一性原理精算',
  S8_qe_phonons: '声子稳定性验证', S9_epw_tc: '超导温度计算',
};

/* 细粒度阶段键（scheduler 内部把 S7 拆成 S7a…S7g）→ 粗粒度行号/名称。
   逐字匹配失败时按 S<N> 前缀回退：S7c_export_relax → S7_qe_relax_pm。 */
const stageIndex = s => {
  const si = STAGES.indexOf(s);
  if (si !== -1) return si;
  const m = /^(S\d+)/.exec(s || '');
  return m ? STAGES.findIndex(x => x.startsWith(m[1] + '_')) : -1;
};
const stageHuman = s => STAGE_HUMAN[s] ||
  (stageIndex(s) !== -1 ? STAGE_HUMAN[STAGES[stageIndex(s)]] : null) || '结论';

/* S7 内部的细粒子步骤（scheduler EXEC_ORDER 拆分）→ 芯片下方小字 */
const SUBSTEP_HUMAN = {
  S7a_qe_relax_prepare: '准备输入',
  S7b_qe_relax: '结构优化',
  S7c_export_relax: '导出结构',
  S7d_prepare_downstream: '准备精算',
  S7e_pomega_prophet: 'AI 声子数据',
  S7f_pomega_qe: '电子精算',
  S7g_compute_pm: '电声判据',
};
const LABEL_HUMAN = {
  TC_REVIEW_COMPLETE: '闯完全部 9 关，是超导候选 ✅',
  STRUCTURE_REJECT_SINGLE_ELEMENT: '单元素结构（单质），不走本流程，淘汰',
  STRUCTURE_REJECT_F_ELECTRON: '含 f 电子元素（稀土/锕系），当前方法算不准，不走本流程，淘汰',
  GAP_REJECT: 'AI 初筛判为金属（不导电就难有超导），淘汰',
  PROPHET_UNSTABLE: 'AI 判断结构不稳定，淘汰',
  QE_PHONON_UNSTABLE: '量子力学声子验证不通过（结构不稳），淘汰',
  HULL_REJECT: '能量检查不通过（材料易分解），淘汰',
  HULL_UNRESOLVED: '查不到稳定性数据，挂起待查',
  MAGNETIC_REJECT: '磁性太强（干扰超导），淘汰',
  MAGNETIC_INCOMPLETE: '磁性检查未完成，待复查',
  MAGNETIC_CANDIDATE_TENTATIVE: '有磁性迹象，需人工确认',
  PM_REJECT: '电-声耦合检查不达标，淘汰',
  PM_ERROR: '电-声检查出错，标记复查',
  STRUCTURE_ERROR: '结构文件有问题，标记复查',
  PROPHET_ERROR: '声子预筛计算出错，标记复查',
  PROPHET_SOFT_MODE_SUSPECT: '声子谱有软模迹象（结构可能不稳），淘汰',
  MANUAL_REJECT: '人工审查后放弃',
  SINGLE_ELEMENT_REJECT: '单元素结构（单质），不走本流程，淘汰',
  TC_INCOMPLETE: '超导温度计算不完整，需诊断',
  AUTO_DISCARD_FIX_BUDGET: '多次自动修复仍失败，自动淘汰',
  AUTO_DISCARD_DIAG: '诊断确认无修复方向，自动淘汰',
  AUTO_DISCARD_POISON: '同类失败反复出现，自动淘汰',
  AUTO_DISCARD_RUN_ENDED: '批次结束时状态丢失，自动淘汰',
  AUTO_DISCARD_PROBE_FAIL: '结果核验不成立，自动淘汰',
};
/* AUTO_DISCARD_<CASE>（case 级标签拼接）的兜底翻译 */
const labelHuman = l => LABEL_HUMAN[l] ||
  (l && l.startsWith('AUTO_DISCARD_') ? '自动修复多次仍失败，自动淘汰' : null);
const name_of = c => c.material_id || c.formula || `#${c.ordinal}`;

/* 已跑时长：进行中 → now-started；已终态 → updated-started（定格） */
export const fmtDur = ms => {
  if (!(ms > 0)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return '刚刚开始';
  if (m < 60) return `${m}分`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}时${m % 60 ? `${m % 60}分` : ''}`;
  return `${Math.floor(h / 24)}天${h % 24}时`;
};

const RUN_STATUS_HUMAN = {
  pending: '准备中', running: '进行中', paused: '已暂停',
  completed: '已完成', failed: '异常结束',
};

function translateEvent(e) {
  const m = e.message || '';
  if (m.startsWith('reconcile:')) return null;
  const rm = m.match(/^run (\S+) → (\S+)$/);
  if (rm) {
    const act = {running: '开始跑', completed: '全部结束',
                 failed: '异常结束', paused: '暂停'}[rm[2]];
    // 流式 run 名（demo10-s0003）折叠回组名，避免同类消息刷屏
    return act && {tag: '批次', level: e.level,
                   text: `筛选批次「${rm[1].replace(/-s\d+$/, '')}」${act}`};
  }
  if (e.kind === 'needs_human')
    return {tag: '需人工', level: 'error', text: '有个计算系统自己修不了，已通知工程师（诊断页看详情）'};
  if (e.kind === 'job_failed')
    return {tag: '故障', level: 'error', text: '一处计算出了问题，AI 正在自动诊断…'};
  if (m.includes('已批准并重排队'))
    return {tag: '修复', level: 'info', text: '批准了修复方案，任务已重新排队计算'};
  return {tag: e.kind || e.level, level: e.level, text: m};
}

const POOL_H = 96, ROW_H = 86, EXIT_H = 88, REJECT_W = 110, PAD = 20;
const CHIP_W = 66, CHIP_H = 27, GAP = 7;

/* ---------- 真实数据 → 分子 ---------- */

function moleculesFromData(cands) {
  return (cands || []).map(c => {
    const si = stageIndex(c.current_stage);
    let lane = 'active', row = 0;
    if (c.status === 'pending' && si === -1) { lane = 'pool'; row = -1; }
    else if (c.status === 'rejected' || c.status === 'quarantine') {
      lane = 'reject'; row = Math.max(0, si);
    } else if (c.status === 'done' || c.status === 'passed') {
      lane = 'winner'; row = STAGES.length;
    } else {
      row = Math.max(0, si);
    }
    return {
      id: 'c' + c.id, realId: c.id,
      formula: c.formula || c.material_id || `#${c.ordinal}`,
      lane, row, status: c.status,
      // 当前阶段 job 状态：running=在跑 / queued=排队 / 其他=已完等收编
      state: c.job_status === 'running' || c.job_status === 'dispatched'
        ? 'running'
        : c.job_status === 'queued' ? 'queued'
        : (lane === 'active' ? 'done-wait' : ''),
      sub: SUBSTEP_HUMAN[c.current_stage] || '',
      label: c.terminal_label || c.status,
      tc: c.tc || null,
      started: c.run_started_at,
      ended: c.terminal_label ? c.updated_at : null,
    };
  });
}

/* ---------- 演示模拟（按真实调度语义） ---------- */

const DEMO_REJECT_P = [0.32, 0.14, 0.24, 0.10, 0.14, 0.14, 0.20, 0.24, 0.12];
const DEMO_NAMES = ['MgB2', 'LaH10', 'H3S', 'YB6', 'CaH6', 'Li2B', 'ScH3',
  'YH6', 'BaH12', 'AlH3', 'NaB3', 'KH5', 'CaB6', 'Mg2C', 'SrH4', 'BH',
  'Li3N', 'C4S', 'TiH2', 'ZrB2', 'HfC', 'WN2', 'SiO2', 'FeB4'];

function useDemo(on) {
  const [mols, setMols] = useState([]);
  const seq = useRef(0);
  const batchStep = useRef({});

  useEffect(() => {
    if (!on) { setMols([]); return; }
    const init = [];
    for (let i = 0; i < 24; i++) {
      const inPool = i >= 8;
      init.push({
        id: 'd' + (seq.current++),
        formula: DEMO_NAMES[i % DEMO_NAMES.length] + (i >= DEMO_NAMES.length ? '*' : ''),
        lane: inPool ? 'pool' : 'active',
        row: inPool ? -1 : Math.floor(Math.random() * 3),
        state: 'running', label: 'demo', delay: Math.random(),
      });
    }
    setMols(init);
    batchStep.current = {};

    const t = setInterval(() => {
      setMols(prev => {
        // batch 层：每 tick 计数一次，满 3 tick 整波推进
        const rows = new Set(
          prev.filter(m => m.lane === 'active')
              .map(m => m.row).filter(r => STAGE_KINDS[r] === 'batch'));
        const advanceWave = {};
        for (const r of rows) {
          batchStep.current[r] = (batchStep.current[r] || 0) + 1;
          if (batchStep.current[r] >= 3) {
            batchStep.current[r] = 0;
            advanceWave[r] = true;
          }
        }
        // case 层：全局并发槽（按层序先到先得）
        const runningIds = new Set();
        for (const m of prev.filter(x => x.lane === 'active')
                            .sort((a, b) => a.row - b.row)) {
          if (STAGE_KINDS[m.row] === 'case' && runningIds.size < MAX_RUNNING) {
            runningIds.add(m.id);
          }
        }

        const out = [];
        const advance = m => {
          if (Math.random() < DEMO_REJECT_P[m.row]) {
            out.push({...m, lane: 'reject', label: 'REJECTED'});
          } else if (m.row >= STAGES.length - 1) {
            out.push({...m, lane: 'winner', label: 'TC_REVIEW_COMPLETE'});
          } else {
            out.push({...m, row: m.row + 1});
          }
        };
        for (const m of prev) {
          if (m.lane === 'pool' || m.lane === 'winner') { out.push(m); continue; }
          if (m.lane === 'reject') { out.push(m); continue; }
          if (STAGE_KINDS[m.row] === 'batch') {
            const m2 = {...m, state: 'running'};   // 批量 job 内都在算
            if (advanceWave[m2.row]) advance(m2);
            else out.push(m2);
          } else if (runningIds.has(m.id)) {
            const m2 = {...m, state: 'running'};
            if (Math.random() < 0.45) advance(m2);
            else out.push(m2);
          } else {
            out.push({...m, state: 'queued'});  // 等 job 槽位
          }
        }
        // 淘汰区每层只留最近 5 个
        const seen = {};
        const pruned = out.filter(m => {
          if (m.lane !== 'reject') return true;
          seen[m.row] = (seen[m.row] || 0) + 1;
          return seen[m.row] <= 5;
        });
        // 从池补位；池太空补新分子
        const stillPool = pruned.filter(m => m.lane === 'pool');
        const actCount = pruned.filter(m => m.lane === 'active').length;
        if (actCount < 14 && stillPool.length) {
          return pruned.map(x => x.id === stillPool[0].id
            ? {...x, lane: 'active', row: 0, state: 'queued'} : x);
        }
        if (pruned.filter(m => m.lane === 'pool').length < 6 && seq.current < 300) {
          pruned.push({
            id: 'd' + (seq.current++),
            formula: DEMO_NAMES[seq.current % DEMO_NAMES.length] + '*',
            lane: 'pool', row: -1, label: 'demo', delay: Math.random(),
          });
        }
        return pruned;
      });
    }, 1100);
    return () => clearInterval(t);
  }, [on]);

  return mols;
}

/* ---------- 布局 ---------- */

function computePositions(mols, width, dims = {pool: POOL_H, row: ROW_H,
                                               exit: EXIT_H}) {
  const funnelW = width - REJECT_W - PAD * 2;
  const rowTop = r => dims.pool + r * dims.row;
  const rowInnerW = r => Math.max(140, funnelW * (1 - r * 0.045));
  const rowX0 = r => PAD + (funnelW - rowInnerW(r)) / 2;

  const groups = {};
  for (const m of mols) {
    const key = m.lane === 'pool' ? 'pool' : m.lane === 'winner' ? 'winner'
      : `${m.lane}:${m.row}`;
    (groups[key] = groups[key] || []).push(m);
  }

  const pos = new Map();
  // maxLines 限排数：芯片绝不允许溢出到下一层的标题条上。
  // 行高不够两层时只排一层
  const maxLines = dims.row >= 78 ? 2 : 1;
  const place = (arr, x0, y0, maxW, lines) => {
    const perLine = Math.max(1, Math.floor((maxW - GAP) / (CHIP_W + GAP)));
    const shown = arr.slice(0, perLine * lines);
    shown.forEach((m, i) => {
      const line = Math.floor(i / perLine);
      const col = i % perLine;
      pos.set(m.id, {
        x: x0 + col * (CHIP_W + GAP),
        y: y0 + line * (CHIP_H + GAP + 1),
      });
    });
  };

  place(groups.pool || [], PAD, 32, width - PAD * 2, 2);
  for (let r = 0; r < STAGES.length; r++) {
    place(groups[`active:${r}`] || [], rowX0(r) + 6, rowTop(r) + 18,
          rowInnerW(r) - 24, maxLines);
    // 淘汰芯片不再占画布：改到每关右侧的滚动列表（rejectByRow）
  }
  // 出口超导候选按 Tc 从高到低排（没算出 Tc 的排最后，按候选 id 新的在前）；
  // 芯片只展示 Top-20，全量数字走全局总数（见下方 winners）
  const WINNER_SHOWN = 20;
  const winners = (groups.winner || []).slice().sort((a, b) => {
    const ta = a.tc?.tc_K, tb = b.tc?.tc_K;
    if (ta != null && tb != null) return tb - ta;
    if (ta != null) return -1;
    if (tb != null) return 1;
    return b.realId - a.realId;
  });
  place(winners.slice(0, WINNER_SHOWN), PAD + 30, rowTop(STAGES.length) + 24,
        width - PAD * 3, 1);
  return {pos, rowTop, rowInnerW, rowX0};
}

/* ---------- 页面 ---------- */

/* 侧栏可折叠面板：点标题收起/展开 */
function SidePanel({title, className = '', children}) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`panel side-panel${open ? '' : ' folded'} ${className}`}>
      <h3 onClick={() => setOpen(o => !o)}>
        {title}<span className="fold-arrow">{open ? '▾' : '▸'}</span>
      </h3>
      {open && children}
    </div>
  );
}

function StageRow({r, width, rowTop, rowInnerW, rowX0, counts, rowH,
                   rejectMols, onOpen}) {
  const isBatch = STAGE_KINDS[r] === 'batch';
  const activeCls = counts.active
    ? (isBatch ? ' active-batch' : ' active-case') : '';
  const bandH = rowH - 6;
  return (
    <>
      <div className={`stage-band${activeCls}`}
           style={{top: rowTop(r), left: rowX0(r), width: rowInnerW(r),
                   height: bandH}}>
        <span className="stage-name">
          {STAGE_SHORT[r]}
          <span className={`stage-hw ${STAGE_HW[r] === 'GPU' ? 'hw-gpu'
              : STAGE_HW[r] === 'CPU' ? 'hw-cpu' : 'hw-mix'}`}
                title={STAGE_HW_TITLE[STAGE_HW[r]]}>{STAGE_HW[r]}</span>
        </span>
        <span className={`stage-count ${isBatch ? 'batch' : ''}`}>
          {isBatch ? '批量 ' : ''}
          {counts.active || ''}
        </span>
      </div>
      {r < STAGES.length - 1 && (
        <div className="flow-line"
             style={{left: rowX0(r) + rowInnerW(r) / 2,
                     top: rowTop(r) + bandH, height: 18}} />
      )}
      <div className="reject-slot" style={{top: rowTop(r) + 2,
                                           left: width - REJECT_W,
                                           height: rowH - 8}}
           title={(() => {
             const items = Object.entries(counts.rejectDetail || {})
               .filter(([, n]) => n > 0)
               .map(([l, n]) => `${labelHuman(l) || l} ${n}`)
               .join(' · ');
             return items ? `累计淘汰原因：${items}` : undefined;
           })()}>
        <span className="reject-count">淘汰 {counts.reject || 0}</span>
        {rejectMols &&
          <div className="reject-list">
            <MoleculeChip m={rejectMols} onClick={() => onOpen(rejectMols)} />
          </div>}
      </div>
    </>
  );
}

export default function Pipeline() {
  const [demo, setDemo] = useState(false);
  const [modal, setModal] = useState(null);
  const {data: cands} = usePolling('/candidates', {limit: 400}, 8000);
  // 在跑/等下一关的候选单独拉：主列表按最新优先分页，大批 pending 投喂后
  // 老 id 的 active 候选会被挤出页，关卡行就空了（同 done 出口框的道理）
  const {data: activeData} = usePolling('/candidates', {status: 'active', limit: 80}, 8000);
  const {data: passedData} = usePolling('/candidates', {status: 'passed', limit: 80}, 30000);
  // 超导候选（done）单独拉：主列表按最新优先分页，老 id 的 done 候选
  // 会被新数据挤出页，出口框就空了——不能只依赖主列表。
  // ordering=tc 由后端按 S9 Tc 全局排序，否则 id 倒序窗口会把
  // 老 id 的高 Tc 候选挤掉，出口框显示的不是最高 Tc
  // 超导候选（done）单独拉：主列表按最新优先分页，老 id 的 done 候选
  // 会被新数据挤出页，出口框就空了——不能只依赖主列表。
  // ordering=tc 由后端按 S9 Tc 全局排序，取前 20 条即为 Tc 最高的芯片；
  // 数字不在这里（读 overview 的全局 done 总数），所以 limit 不需要
  // 跟随 done 总数增长
  const {data: winnersData} = usePolling('/candidates',
      {status: 'done', limit: 20, ordering: 'tc'}, 30000);
  const {data: ov} = usePolling('/overview', null, 8000);
  const {data: events} = usePolling('/events', {limit: 14}, 6000);
  const {data: cluster} = usePolling('/cluster_status', null, 8000);
  // 已跑时长每 30s 刷新一次（数据轮询 8s 也会带动重渲染）
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const real = useMemo(() => {
    const seen = new Set();
    const merged = [];
    for (const c of [...(winnersData || []), ...(activeData || []),
                     ...(passedData || []), ...(cands || [])]) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      merged.push(c);
    }
    return moleculesFromData(merged);
  }, [cands, winnersData, activeData, passedData]);
  // 运行时间用全局口径（所有 run）；老后端没有 run_timing_all 时退回整批
  const bt = ov?.run_timing_all || ov?.batch_timing;
  const demoMols = useDemo(demo);
  const mols = useMemo(
    () => (demo ? [...real, ...demoMols] : real), [demo, real, demoMols]);

  // 每关只展示最新一个淘汰样品（数字走聚合看全量）。
  // 样品由后端按关卡各取最新一个（全局口径）——前端窗口式轮询会把
  // 冷门关卡（如 S9）的最新淘汰挤出窗口。演示模式下仿真淘汰优先。
  const rejectByRow = useMemo(() => {
    const g = {};
    for (const [key, c] of Object.entries(ov?.rejected_latest_by_stage || {})) {
      const si = stageIndex(key);
      if (si === -1) continue;
      g[si] = {id: 'c' + c.id, realId: c.id,
               formula: c.formula || c.material_id || `#${c.id}`,
               lane: 'reject', row: si, status: 'rejected',
               label: c.terminal_label || 'rejected',
               ended: c.updated_at};
    }
    if (demo)
      for (const m of demoMols)
        if (m.lane === 'reject' && m.row >= 0) g[m.row] = m;
    return g;
  }, [ov, demo, demoMols]);

  const wrapRef = useRef(null);
  const [width, setWidth] = useState(900);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // 漏斗行高随视口压缩：整页尽量不出现滚动条
  const [vh, setVh] = useState(window.innerHeight);
  useEffect(() => {
    const on = () => setVh(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  const dims = useMemo(() => {
    const avail = vh - 150;  // 标题 + 说明 + 页边距
    const row = Math.max(56, Math.min(ROW_H, Math.floor(
      (avail - POOL_H - EXIT_H) / STAGES.length)));
    return {pool: POOL_H, row, exit: EXIT_H};
  }, [vh]);

  const layout = useMemo(() => computePositions(mols, width, dims),
                         [mols, width, dims]);

  const counts = useMemo(() => {
    const c = {};
    for (const m of mols) {
      if (m.lane === 'pool' || m.lane === 'winner') continue;
      const key = `${m.lane}:${m.row}`;
      c[key] = (c[key] || 0) + 1;
    }
    return c;
  }, [mols]);

  const run = ov?.latest_run;
  const streamGroup = run?.config_snapshot?.stream_group;
  // 候选状态面板用全局口径（所有批次），不收窄到最新 stream_group
  const statusBy = ov?.candidates_by_status_all || ov?.candidates_by_status || {};
  const total = Object.values(statusBy).reduce((a, b) => a + b, 0);
  // 淘汰数用全局聚合（按关卡）；画布芯片只是最新批次的样品，老批次被
  // 挤出窗口后芯片数会归零，数字不能依赖它
  const rejectAgg = useMemo(() => {
    const raw = ov?.rejected_by_stage_all || {};
    const byStage = {};
    for (const [k, v] of Object.entries(raw)) {
      const si = stageIndex(k);
      const key = si !== -1 ? STAGES[si] : k;
      byStage[key] = (byStage[key] || 0) + v;
    }
    return byStage;
  }, [ov]);
  // 关卡 × 淘汰原因（细粒度键归并到粗粒度关卡）
  const rejectLabelAgg = useMemo(() => {
    const raw = ov?.rejected_by_stage_label_all || {};
    const byStage = {};
    for (const [k, labels] of Object.entries(raw)) {
      const si = stageIndex(k);
      const key = si !== -1 ? STAGES[si] : k;
      byStage[key] = byStage[key] || {};
      for (const [l, n] of Object.entries(labels))
        byStage[key][l] = (byStage[key][l] || 0) + n;
    }
    return byStage;
  }, [ov]);
  // 出口框数字读全局 done 总数（overview.candidates_done_all）——本地分子
  // 列表是分页窗口，数字会随窗口漂移；没有该字段的老后端才退回本地计数
  const winners = ov?.candidates_done_all ??
      mols.filter(m => m.lane === 'winner').length;
  const height = dims.pool + STAGES.length * dims.row + dims.exit;

  // 最新动态：终态里程碑（candidates）+ 翻译后的事件，按时间混排。
  // 今天内的条目只显示时分秒，更早的带日期（MM-DD）
  const today = new Date().toLocaleDateString('sv');
  const feed = useMemo(() => {
    const items = [];
    for (const c of (cands || [])) {
      const lh = labelHuman(c.terminal_label);
      if (!lh) continue;
      items.push({
        key: `cand-${c.id}`, ts: (c.updated_at || '').slice(0, 19),
        level: c.status === 'done' ? 'info' : 'warn',
        tag: stageHuman(c.current_stage),
        text: `「${name_of(c)}」${lh}`,
      });
    }
    for (const e of (events || [])) {
      const t = translateEvent(e);
      if (t) items.push({key: `ev-${e.id}`, ts: (e.created_at || '').slice(0, 19), ...t});
    }
    items.sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
    // 流式组会产生大量同类事件（10 个 run 同一动作），同类消息只留最新一条
    const seen = new Set();
    return items.filter(it => {
      const k = `${it.tag}|${it.text}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).slice(0, 50);
  }, [cands, events]);
  const feedTime = ts => !ts ? '' :
    (ts.slice(0, 10) === today ? ts.slice(11) : ts.slice(5, 16).replace('T', ' '));

  // 正在做哪一关（取最深的有关在跑候选的关卡）。
  // candidates_by_stage 的键可能是细粒度阶段（S7c…），先归并到粗粒度。
  const byStageRaw = ov?.candidates_by_stage || {};
  const byStage = {};
  for (const [k, v] of Object.entries(byStageRaw)) {
    const si = stageIndex(k);
    const key = si !== -1 ? STAGES[si] : k;
    byStage[key] = (byStage[key] || 0) + v;
  }
  const nowStage = [...STAGES].reverse().find(s => byStage[s] > 0);

  // 出口庆祝：新分子到达 → 出口闪光 + 粒子迸发
  const prevWinners = useRef(winners);
  const [bursts, setBursts] = useState([]);
  useEffect(() => {
    if (winners > prevWinners.current) {
      const id = Date.now();
      const sparks = Array.from({length: 14}, (_, i) => ({
        id: `${id}-${i}`,
        dx: (Math.random() - 0.5) * 140,
        dy: -Math.random() * 90 - 10,
        gold: Math.random() < 0.3,
      }));
      setBursts(b => [...b, {id, sparks}]);
      setTimeout(() => setBursts(b => b.filter(x => x.id !== id)), 1000);
    }
    prevWinners.current = winners;
  }, [winners]);

  return (
    <div>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center',
                   gap: 14, position: 'relative', marginBottom: 6}}>
        <h2 className="page-title" style={{margin: 0}}>超导筛选流水线</h2>
        {run && <span className="muted">
          {streamGroup ? '流式筛选' : '批次'}「{streamGroup || run.name}」
          {' '}· <b>{RUN_STATUS_HUMAN[run.status] || run.status}</b>
          {' '}· 共 {total} 个候选材料（含全部批次）
        </span>}
        <label style={{position: 'absolute', right: 0, display: 'flex', gap: 6,
                       alignItems: 'center', cursor: 'pointer'}}>
          <input type="checkbox" checked={demo}
                 onChange={e => setDemo(e.target.checked)} />
          演示模式 {demo && <span className="badge running">DEMO</span>}
        </label>
      </div>
      <p className="muted" style={{margin: '0 0 10px', fontSize: 12.5, textAlign: 'center'}}>
        候选晶体从分子池出发依次闯 9 关：AI 快速初筛（对称性/带隙/声子）→
        第一性原理精算（VASP/QE）→ EPW 求解超导温度 Tc。
        淘汰的落入右侧灰色出口；点击分子可看 3D 结构。
      </p>

      <div className="pipeline-layout">
        {/* 侧栏高度钳到与漏斗一致：feed 条目多时内部滚动，不再把页面撑出单屏 */}
        <div className="pipeline-side" style={{maxHeight: height}}>
          {bt && bt.started_at &&
            <div className="panel">
              <h3>总运行时间</h3>
              <div className="stat-cards" style={{marginBottom: 0}}>
                <div className="stat-card" style={{minWidth: 110, flex: 1, padding: '10px 14px'}}>
                  <div className="num" style={{fontSize: 20}}>
                    {fmtDur(new Date(bt.finished_at || now).getTime()
                            - new Date(bt.started_at).getTime())}
                  </div>
                  <div className="lbl">
                    {bt.finished_at ? '已全部结束' : '持续进行中'}
                  </div>
                </div>
                <div className="stat-card" style={{minWidth: 110, flex: 1, padding: '10px 14px'}}>
                  <div className="num" style={{fontSize: 20}}>
                    {ov?.candidates_terminal_all ?? 0}/{ov?.candidates_total_all ?? '…'}
                  </div>
                  <div className="lbl">
                    已出结果
                  </div>
                </div>
              </div>
            </div>}
          <SidePanel title="候选状态">
            <dl className="kv">
              {STATUS_GROUPS.map(g => {
                const n = g.items.reduce((a, s) => a + (statusBy[s] || 0), 0);
                const detail = g.items.length > 1 && g.items
                  .filter(s => statusBy[s] > 0)
                  .map(s => `${STATUS_SUB_HUMAN[s]} ${statusBy[s]}`).join(' · ');
                return (
                  <div key={g.key} style={{display: 'contents'}}
                       title={g.items.map(s => `${s}:${statusBy[s] || 0}`).join('  ')}>
                    <dt style={n ? undefined : {opacity: 0.4}}>{g.name}</dt>
                    <dd><b style={n ? undefined : {opacity: 0.4}}>{n}</b>
                      {detail && <span className="muted" style={{fontSize: 11.5}}>（{detail}）</span>}
                    </dd>
                  </div>);
              })}
            </dl>
          </SidePanel>
          <SidePanel title="机器状态">
            <div className="muted" style={{fontSize: 11.5, marginBottom: 6}}>
              GPU 机 {cluster?.gpu?.online ? '· 在线' : '· 离线'}
            </div>
            {cluster?.gpu?.online ? cluster.gpu.gpus.map(g => (
              <div key={g.index} style={{marginBottom: 7}}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: 12}}>
                  <span>GPU {g.index}</span>
                  <span className="muted">{g.util}% · {Math.round(g.mem_used / 1024)}/{Math.round(g.mem_total / 1024)}G</span>
                </div>
                <div style={{height: 5, background: 'var(--panel2)', borderRadius: 3, overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${g.util}%`, opacity: 0.85,
                               background: g.util >= 95 ? 'var(--red)' : 'var(--blue)'}} />
                </div>
                <div style={{height: 3, marginTop: 2, background: 'var(--panel2)', borderRadius: 2, overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${g.mem_total ? 100 * g.mem_used / g.mem_total : 0}%`,
                               background: 'var(--purple)'}} />
                </div>
              </div>
            )) : <p className="muted">连接不上 GPU 机器</p>}
            <div style={{marginTop: 8, fontSize: 12.5}}>
              {(cluster?.k8s_nodes || []).map((n, i) => (
                <div key={n.name} style={{marginBottom: 7}}
                     title={n.cpu_pct != null ? 'K8s 节点实时利用率' : '利用率数据缺失'}>
                  <div style={{fontSize: 12}}>
                    <span style={{color: n.ready ? 'var(--green)' : 'var(--red)'}}>●</span> K8s 节点 {i + 1}
                    {n.cpu_pct != null && <span className="muted"> · CPU {n.cpu_used?.toFixed(1)}/{n.cpu_cap} 核</span>}
                    {n.mem_pct != null && <span className="muted"> · 内存 {n.mem_used_gib}/{n.mem_cap_gib}G</span>}
                  </div>
                  {n.cpu_pct != null &&
                    <div style={{height: 4, marginTop: 3, background: 'var(--panel2)', borderRadius: 2, overflow: 'hidden'}}>
                      <div style={{height: '100%', width: `${n.cpu_pct}%`, background: 'var(--blue)', opacity: 0.85}} />
                    </div>}
                  {n.mem_pct != null &&
                    <div style={{height: 4, marginTop: 2, background: 'var(--panel2)', borderRadius: 2, overflow: 'hidden'}}>
                      <div style={{height: '100%', width: `${n.mem_pct}%`, background: 'var(--purple)'}} />
                    </div>}
                </div>))}
              {cluster?.jobs &&
                <div className="muted" style={{fontSize: 11.5, marginTop: 3}}>
                  计算 job {cluster.jobs.compute}/{cluster.max_parallel_jobs || 3} 在跑 ·
                  {' '}管理任务 {cluster.jobs.mgmt} 个
                </div>}
            </div>
          </SidePanel>

          <SidePanel title="最新动态" className="feed-panel">
            <div className="feed-scroll">
            {nowStage &&
              <p style={{margin: '0 0 8px', fontSize: 13}}>
                正在做「{STAGE_HUMAN[nowStage]}」：<b>{byStage[nowStage]}</b> 个候选
              </p>}
            {feed.map(item => (
              <div className="eventline" key={item.key}>
                <span className="t">{feedTime(item.ts)}</span>
                <span className={`badge ${item.level}`}>{item.tag}</span>
                <span> {item.text}</span>
              </div>))}
            {feed.length === 0 && !nowStage &&
              <p className="muted">还没有动态——发一个筛选批次后这里会实时更新</p>}
            </div>
          </SidePanel>
        </div>

        <div className="panel pipeline-funnel" ref={wrapRef}
             style={{height, position: 'relative', overflow: 'hidden'}}>
          <div className="pool-box" style={{left: PAD, width: width - PAD * 2}}>
            <span className="pool-label">
              {/* 池子数量用 overview 真实总数（拉回来的只是渲染样本） */}
              ▼ 分子池<b>{statusBy.pending != null ? statusBy.pending
                          : mols.filter(m => m.lane === 'pool').length || ''}</b>
            </span>
          </div>

          {STAGES.map((s, r) => (
            <StageRow key={s} r={r} width={width} rowTop={layout.rowTop}
                      rowInnerW={layout.rowInnerW} rowX0={layout.rowX0}
                      rowH={dims.row}
                      rejectMols={rejectByRow[r]}
                      onOpen={setModal}
                      counts={{active: counts[`active:${r}`],
                               reject: rejectAgg[s] ?? counts[`reject:${r}`] ?? 0,
                               rejectDetail: rejectLabelAgg[s] || {}}} />
          ))}

          <div className="flow-line"
               style={{left: layout.rowX0(STAGES.length - 1) +
                              layout.rowInnerW(STAGES.length - 1) / 2,
                       top: layout.rowTop(STAGES.length - 1) + dims.row - 6,
                       height: 18}} />
          <div className={`exit-box${bursts.length ? ' flash' : ''}`}
               style={{top: layout.rowTop(STAGES.length),
                       left: PAD, width: width - PAD * 2}}>
            <span className="exit-label">★ 超导候选 <b>{winners}</b></span>
          </div>
          {bursts.map(b => (
            <div key={b.id} style={{position: 'absolute',
                                    left: PAD + (width - PAD * 2) / 2,
                                    top: layout.rowTop(STAGES.length) + 30,
                                    pointerEvents: 'none'}}>
              {b.sparks.map(s => (
                <div key={s.id}
                     className={`spark${s.gold ? ' gold' : ''}`}
                     style={{'--dx': `${s.dx}px`, '--dy': `${s.dy}px`}} />
              ))}
            </div>))}

          {mols.map(m => {
            if (m.lane === 'reject') return null;  // 已在右侧滚动列表渲染
            const p = layout.pos.get(m.id);
            if (!p) return null;
            const dur = m.started
              ? fmtDur(new Date(m.ended || now).getTime()
                       - new Date(m.started).getTime())
              : '';
            const tcTxt = m.tc?.tc_K != null
              ? `Tc ${Number(m.tc.tc_K).toFixed(2)} K` : '';
            // 出了 Tc 就只显示 Tc（阶段/时长挤在一起会压到邻格）
            const subTxt = tcTxt || [m.sub, dur].filter(Boolean).join(' · ');
            return (
              <div key={m.id} className="mol-slot"
                   style={{transform: `translate(${p.x}px, ${p.y}px)`}}>
                <MoleculeChip m={m} onClick={() => setModal(m)} />
                {subTxt &&
                  <div className="chip-sub">{subTxt}</div>}
              </div>);
          })}
        </div>
      </div>

      {modal && <StructureModal m={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
